// src/AppContent.js
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Routes, Route } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Contexts
import { useAuth } from './context/AuthContext';
import { useData } from './context/DataContext';
import JobResultListener from './components/JobResultListener'; // Bridge between the DataContext and JobContext

// Navbars & Layout Components
import Navbar from './components/Navbar'; // Main app navbar
import AuthNavbar from './components/Auth/AuthNavbar'; // Navbar for auth flows
import SocialMediaSidebar from './components/SocialMediaSidebar'; //Sidebar for configuration flows
import OfflinePage from './components/OfflinePage';

// Route Groups
import AuthenticatedRoutes from './routes/AuthenticatedRoutes';
import UnauthenticatedRoutes from './routes/UnauthenticatedRoutes';

// Page Components (for direct routing in AppContent)
import ResetPasswordPage from './components/Auth/ResetPasswordPage';
import VerifyEmailPage from './components/Auth/VerifyEmailPage';

// UI Components
import ErrorDisplayModal from './components/ErrorDisplayModal';
import './components/ErrorDisplayModal.module.css';
import LoadingComponent from './components/LoadingComponent';

// Modals (conditionally rendered)
import ProductModal from './components/ProductModal';
import LlmConfigModal from './components/LlmConfigModal';
import LlmProviderModelModal from './components/LlmProviderModelModal';
import CompanyModal from './components/CompanyModal';
import UserProfileModal from './components/UserProfileModal';
import AdminTasksModal from './components/AdminTasksModal';
import DiscoverySettingsModal from './components/DiscoverySettingsModal';
import AnalyticsDashboardModal from './components/AnalyticsDashboardModal';
import LegalGateModal from './components/LegalGateModal';
import SuperAdminModal from './components/SuperAdminModal';
import SuspensionScreen from './components/SuspensionScreen';
import WalletBanner from './components/WalletBanner';

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();

  // --- Context Hooks ---
  const { currentUser, authLoading, logout } = useAuth();
  const {
    userProfile,
    error, // Global error from DataContext
    setError: setGlobalError, // Setter for global error
    clearError: clearGlobalError,
    loading: dataContextLoading, // Generic loading from DataContext (e.g., for page content in AuthenticatedRoutes)
    isOffline,
    isRetrying,
    handleRetryConnection,
    fetchSocialMediaConnections,
    fetchLlmConfigurations,
    fetchUserSettings,
    fetchAdminLlmConfigurations,
    fetchTaskTemplates,
    fetchProducts,
    setSelectedDate,
    pendingAgreements,
    fetchComplianceStatus,
  } = useData();

  // --- Local State for AppContent specific data fetching status ---
  const [hasFetchedUserData, setHasFetchedUserData] = useState(false);
  const [hasFetchedAdminData, setHasFetchedAdminData] = useState(false);
  /*const [isFinalizingVerification, setIsFinalizingVerification] =
    useState(false); // <-- New state*/
  const isFinalizingVerification = false;

  // 1. Get status from userProfile
  const accountStatus = userProfile?.accountStatus || 'ACTIVE';
  const daysLeft = userProfile?.gracePeriodDaysLeft;
  const invoiceLink = userProfile?.latestInvoiceLink;

  // Inside the component, near your other derivations
  const isAdminUser = userProfile?.role === 'Admin';
  const walletBalance = userProfile?.walletBalance;
  const companyId = userProfile?.companyId; 
  const isSuspended = walletBalance !== undefined && walletBalance <= -50.00;

  useEffect(() => {
    console.log('[AppContent] Location changed to:', location.pathname);
  }, [location]);

  // --- DEBUGGING LOGS ---
  console.log(
    '[AppContent] Rendering. Path:',
    location.pathname,
    'currentUser:',
    !!currentUser,
    'authLoading:',
    authLoading,
    'userProfile:',
    !!userProfile,
    'isEmailVerified:',
    currentUser?.emailVerified,
    'isFinalizingVerification:',
    isFinalizingVerification,
    'isOffline:',
    isOffline,
    'GlobalError:',
    error,
  );

  // --- URL Params & Route Checks ---
  const queryParams = new URLSearchParams(location.search);
  const isActionCodeFlow =
    queryParams.has('oobCode') && queryParams.has('mode');
  const isForgotPasswordRoute = location.pathname === '/forgot-password';
  const isVerifyEmailRoute = location.pathname === '/verify-email';
  const isHandlingActionCode = isForgotPasswordRoute && isActionCodeFlow;
  const isOAuthCallback =
    location.pathname === '/social-media' && queryParams.has('code');

  // --- Derived States for Clarity ---
  const isAuthenticatedAtFirebase = !!currentUser;
  const isAppUserProfileLoaded = !!userProfile;
  const isEmailVerifiedByFirebase =
    currentUser?.emailVerified || userProfile?.isImpersonated === true;

  // --- Loading States for AppContent's Main Spinner ---
  const isCoreSessionDataLoaded =
    isAuthenticatedAtFirebase && isAppUserProfileLoaded;
  const requiredAppUserDataFetched = hasFetchedUserData;
  const requiredAdminRoleDataFetched = isAdminUser ? hasFetchedAdminData : true; // True if not admin, so doesn't block

  const showSpinnerForFinalizingVerification =
    isFinalizingVerification &&
    isAuthenticatedAtFirebase &&
    !isEmailVerifiedByFirebase;

  const showMainAppSpinner =
    (isOAuthCallback && !isAuthenticatedAtFirebase) ||
    (!isHandlingActionCode &&
      !isVerifyEmailRoute &&
      !error &&
      (authLoading ||
        showSpinnerForFinalizingVerification || // Prioritize this if it's true
        (isAuthenticatedAtFirebase && // Only check these if not finalizing verification and auth is done
          (!isAppUserProfileLoaded ||
            !requiredAppUserDataFetched ||
            !requiredAdminRoleDataFetched))));

  // 2. Add a useEffect to trigger compliance check after verification
  useEffect(() => {
    if (currentUser?.emailVerified && userProfile) {
      fetchComplianceStatus();
    }
  }, [currentUser?.emailVerified, userProfile, fetchComplianceStatus]);

  // Effect to manage isFinalizingVerification based on navigation state and currentUser
  /*useEffect(() => {
    const justVerifiedFlag = location.state?.justVerifiedEmail;

    if (justVerifiedFlag) {
      if (currentUser && !currentUser.emailVerified) {
        console.log(
          '[AppContent] Detected justVerifiedEmail flag; email NOT YET verified in currentUser. Setting isFinalizingVerification TRUE.',
        );
        setIsFinalizingVerification(true);
      } else if (currentUser && currentUser.emailVerified) {
        // Email is already verified by the time this runs, or became verified.
        console.log(
          '[AppContent] Detected justVerifiedEmail flag; email IS verified in currentUser. Setting isFinalizingVerification FALSE.',
        );
        setIsFinalizingVerification(false); // Ensure it's false if already verified
      }
      // Clear the flag from location state in either case to prevent re-triggering
      const { justVerifiedEmail, ...restState } = location.state;
      navigate(
        location.pathname,
        {
          replace: true,
          state: Object.keys(restState).length > 0 ? restState : null,
        },
        { replace: true },
      ); // Ensure replace is effective for history
    }
  }, [location.state, currentUser, navigate, location.pathname]);*/

  // --- useEffect for Unverified Email Warning (Global Error) ---
  useEffect(() => {
    const shouldSetUnverifiedError =
      isAuthenticatedAtFirebase &&
      !isEmailVerifiedByFirebase &&
      !isHandlingActionCode &&
      !isVerifyEmailRoute &&
      !isFinalizingVerification;

    // --- START OF THE DEFINITIVE FIX ---
    if (shouldSetUnverifiedError) {
      console.warn(
        '[AppContent] User email not verified... Setting global error.',
      );
      // Always set a structured error object for consistency.
      setGlobalError({
        code: 'EMAIL_NOT_VERIFIED', // A custom, consistent code
        message:
          'Your email is not verified. Please check your email and verify your account. If you need a new verification email, you can request one from the verification page.',
      });
    } else {
      // Safely check if the current error is the one we should clear.
      // We must check that `error` is an object and then check its `message` property.
      if (
        error &&
        typeof error === 'object' &&
        error.message &&
        error.message.includes('Your email is not verified')
      ) {
        console.log(
          "[AppContent] Conditions for 'email not verified' error no longer met. Clearing global error.",
        );
        clearGlobalError();
      }
    }
    // --- END OF THE DEFINITIVE FIX ---
  }, [
    isAuthenticatedAtFirebase,
    isEmailVerifiedByFirebase,
    isHandlingActionCode,
    isVerifyEmailRoute,
    setGlobalError,
    clearGlobalError,
    error, // Dependency is correct
    isFinalizingVerification,
    currentUser?.emailVerified,
  ]);

  // --- useEffects for fetching app-specific data after core session is loaded ---
  useEffect(() => {
    if (
      isCoreSessionDataLoaded &&
      !hasFetchedUserData &&
      !isOffline &&
      !isHandlingActionCode &&
      !isVerifyEmailRoute &&
      !isFinalizingVerification
    ) {
      console.log(
        '[AppContent] Core session loaded. Triggering initial user-specific data fetches...',
      );
      (async () => {
        try {
          await Promise.all([
            fetchSocialMediaConnections(currentUser.uid), // Assuming DataContext methods handle their own loading/error states
            fetchLlmConfigurations(),
            fetchUserSettings(),
            fetchTaskTemplates(),
            fetchProducts(),
          ]);
          console.log(
            '[AppContent] Initial user-specific data fetch completed.',
          );
          console.log('[AppContent] Setting initial To-Do page state.');
          setSelectedDate(new Date()); // Set to today's date
          // The completion filter will default to "not completed" in the ToDoPage component itself.
          // This ensures that on first load/refresh, the user sees today's pending tasks.
          console.log(
            '[AppContent] Initial user-specific data fetch completed.',
          );
          setHasFetchedUserData(true);
        } catch (err) {
          console.error(
            '[AppContent] Error fetching initial user-specific data:',
            err,
          );
          setGlobalError(err);
          setHasFetchedUserData(true); // Mark as attempted to prevent re-fetch loop
        }
      })();
    }
    // Reset flags on logout or if core session becomes invalid
    if (!isCoreSessionDataLoaded && hasFetchedUserData) {
      console.log(
        '[AppContent] Core session lost or user logged out. Resetting hasFetchedUserData.',
      );
      setHasFetchedUserData(false);
    }
  }, [
    isCoreSessionDataLoaded,
    hasFetchedUserData,
    currentUser,
    isOffline,
    fetchSocialMediaConnections,
    fetchLlmConfigurations,
    fetchUserSettings,
    setGlobalError,
    isHandlingActionCode,
    isVerifyEmailRoute,
    isFinalizingVerification,
    setSelectedDate,
    fetchProducts,
    fetchTaskTemplates,
  ]);

  useEffect(() => {
    if (
      isCoreSessionDataLoaded &&
      isAdminUser &&
      !hasFetchedAdminData &&
      !isOffline &&
      !isHandlingActionCode &&
      !isVerifyEmailRoute
    ) {
      console.log(
        '[AppContent] Core session loaded for Admin. Triggering initial admin-specific data fetches...',
      );
      (async () => {
        try {
          await Promise.all([
            fetchAdminLlmConfigurations(),
            fetchTaskTemplates(),
          ]);
          console.log(
            '[AppContent] Initial admin-specific data fetch completed.',
          );
          setHasFetchedAdminData(true);
        } catch (err) {
          console.error(
            '[AppContent] Error fetching initial admin-specific data:',
            err,
          );
          setGlobalError(err);
          setHasFetchedAdminData(true); // Mark as attempted
        }
      })();
    }
    // Reset flags on logout, if user is no longer admin, or core session lost
    if ((!isCoreSessionDataLoaded || !isAdminUser) && hasFetchedAdminData) {
      console.log(
        '[AppContent] Core session lost, user no longer admin, or logged out. Resetting hasFetchedAdminData.',
      );
      setHasFetchedAdminData(false);
    }
  }, [
    isCoreSessionDataLoaded,
    isAdminUser,
    hasFetchedAdminData,
    fetchAdminLlmConfigurations,
    fetchTaskTemplates,
    setGlobalError,
    isHandlingActionCode,
    isVerifyEmailRoute,
    isFinalizingVerification,
    isOffline,
  ]);

  // --- Logout Handler ---
  const handleLogout = async () => {
    try {
      await logout(); // from useAuth()
      // Clear local fetch flags on logout
      setHasFetchedUserData(false);
      setHasFetchedAdminData(false);
      navigate('/'); // Redirect to home/login
    } catch (err) {
      console.error('Logout failed:', err);
      setGlobalError(err);
    }
  };

  // 1. Offline State Check
  if (isOffline) {
    console.log(
      "[AppContent] RENDER PATH: 'isOffline' is true. Rendering <OfflinePage />.",
    );
    return (
      <OfflinePage
        handleRetryConnection={handleRetryConnection}
        isRetrying={isRetrying}
      />
    );
  }

  // 2. Loading State Check
  if (showMainAppSpinner) {
    let spinnerMessage = 'Loading application...';
    if (authLoading) spinnerMessage = 'Authenticating...';
    else if (showSpinnerForFinalizingVerification)
      spinnerMessage = 'Finalizing email verification...';
    else if (!isAuthenticatedAtFirebase)
      spinnerMessage = 'Initializing session...';
    else if (!isAppUserProfileLoaded)
      spinnerMessage = 'Loading user profile...';
    else if (!requiredAppUserDataFetched || !requiredAdminRoleDataFetched)
      spinnerMessage = 'Loading application data...';

    return <LoadingComponent message={spinnerMessage} />;
  }

  // 3. Default: Render the Main Application
  // If we get here, we are online and not loading.
  const sidebarKey =
    userProfile && currentUser ? `user-${currentUser.uid}` : 'loading-profile';

  // This log shows the final check before the render decision is made.
  console.log(
    `[AppContent] RENDER CHECK: About to decide main render path. isOffline is currently: ${isOffline}`,
  );

  return (
    <div
      className="App"
      style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}
    >
      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />

      {/* Global Error Display */}
      {error && (
        <ErrorDisplayModal error={error} clearError={clearGlobalError} />
      )}

      <Routes>
        {/* --- Special Route: /forgot-password --- */}
        <Route
          path="/forgot-password"
          element={
            <>
              <AuthNavbar />
              <div
                style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
              >
                <ResetPasswordPage />
              </div>
            </>
          }
        />

        {/* --- Special Route: /verify-email --- */}
        <Route
          path="/verify-email"
          element={
            <>
              <AuthNavbar />
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {isAuthenticatedAtFirebase ? (
                  <VerifyEmailPage />
                ) : (
                  <LoadingComponent message="Loading verification page..." />
                )}
              </div>
            </>
          }
        />

        {/* --- Catch-all Route: /* --- */}
        <Route
          path="/*"
          element={
            <>
              {!isAuthenticatedAtFirebase ? (
                // Scenario 1: NOT Authenticated
                <>
                  <AuthNavbar />
                  <div style={{ flex: 1 }}>
                    <UnauthenticatedRoutes />
                  </div>
                </>
              ) : !isAppUserProfileLoaded ? (
                // Scenario 2: Authenticated, but Profile NOT YET Loaded
                <>
                  <AuthNavbar />
                  <LoadingComponent message="Finalizing user session..." />
                </>
              ) : !isEmailVerifiedByFirebase ? (
                // Scenario 3: Authenticated, Profile Loaded, BUT Email NOT Verified
                <>
                  <AuthNavbar />
                  <div
                    style={{ flex: 1, padding: '20px', textAlign: 'center' }}
                  >
                    <p>Your email address is not verified.</p>
                    <button onClick={() => navigate('/verify-email')}>
                      Go to Email Verification Page
                    </button>
                  </div>
                  <UserProfileModal />
                </>
              ) : pendingAgreements && pendingAgreements.length > 0 ? (
                // --- NEW Scenario 4: Legal Gate ---
                <>
                  <AuthNavbar />
                  <LegalGateModal agreement={pendingAgreements[0]} />
                </>
              ) : isSuspended ? (
                // --- NEW Scenario 6: Hard Block (-$50 limit) ---
                <SuspensionScreen companyId={companyId} isAdmin={isAdminUser} />
              ) : (
                // --- Scenario 4: Standard App Access ---
                <>
                  {/* Show progressive warnings if balance is low */}
                  <WalletBanner 
                    balance={walletBalance} 
                    companyId={companyId} 
                    isAdmin={isAdminUser} 
                  />

                  <Navbar handleLogout={handleLogout} />
                  <JobResultListener />
                  <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                    <SocialMediaSidebar key={sidebarKey} />
                    <div
                      style={{ flex: 1, overflowY: 'auto', padding: '0rem' }}
                    >
                      <AuthenticatedRoutes loading={dataContextLoading} />
                    </div>
                  </div>
                  {/* Modals */}
                  <UserProfileModal />
                  <LlmConfigModal />
                  <DiscoverySettingsModal />
                  <AnalyticsDashboardModal />
                  {isAdminUser && <ProductModal />}
                  {isAdminUser && <CompanyModal />}
                  {isAdminUser && <AdminTasksModal />}
                  {userProfile?.isSuperAdmin === true && (
                    <LlmProviderModelModal />
                  )}
                  {userProfile?.isSuperAdmin === true && <SuperAdminModal />}
                </>
              )}
            </>
          }
        />
      </Routes>
    </div>
  );
}

export default AppContent;
